// PPTX → markdown extraction (Session 19AD).
//
// PPTX files are zip archives. Slides live in `ppt/slides/slide<N>.xml`. We
// extract `<a:t>...</a:t>` runs and group them per slide as `## Slide N` sections.
//
// Trade-offs:
//   - Tier-1 only: drops formatting / tables / images. Sufficient signal for the
//     LLM (textual content is what extract_cases needs).
//   - Slide notes (`ppt/notesSlides/notesSlide<N>.xml`) are also pulled when
//     present — instructors often put speaker notes there.
import JSZip from 'jszip';
import { ParseError } from '../types.js';
const SLIDE_RE = /^ppt\/slides\/slide(\d+)\.xml$/;
const NOTES_RE = /^ppt\/notesSlides\/notesSlide(\d+)\.xml$/;
const TEXT_RUN_RE = /<a:t[^>]*>([\s\S]*?)<\/a:t>/g;
export async function extractPptx(file) {
    const start = Date.now();
    const warnings = [];
    let zip;
    try {
        zip = await JSZip.loadAsync(await file.arrayBuffer());
    }
    catch (err) {
        throw new ParseError('failed to open .pptx as zip', err);
    }
    const slideEntries = [];
    const notesByNum = new Map();
    for (const path of Object.keys(zip.files)) {
        const slideMatch = path.match(SLIDE_RE);
        if (slideMatch) {
            slideEntries.push({ num: parseInt(slideMatch[1], 10), path });
            continue;
        }
        const notesMatch = path.match(NOTES_RE);
        if (notesMatch) {
            notesByNum.set(parseInt(notesMatch[1], 10), path);
        }
    }
    slideEntries.sort((a, b) => a.num - b.num);
    if (slideEntries.length === 0) {
        warnings.push('no slides found in pptx');
    }
    const sections = [];
    for (const { num, path } of slideEntries) {
        const xml = await zip.file(path).async('text');
        const text = collectRuns(xml);
        let block = `## Slide ${num}\n\n${text || '_(empty slide)_'}`;
        const notesPath = notesByNum.get(num);
        if (notesPath) {
            const notesXml = await zip.file(notesPath).async('text');
            const notesText = collectRuns(notesXml);
            if (notesText) {
                block += `\n\n_Notes_: ${notesText}`;
            }
        }
        sections.push(block);
    }
    return {
        markdown: sections.join('\n\n'),
        meta: {
            sourceFormat: 'pptx',
            tier: 'tier1',
            pageCount: slideEntries.length,
            durationMs: Date.now() - start,
            warnings,
        },
    };
}
function collectRuns(xml) {
    const out = [];
    for (const m of xml.matchAll(TEXT_RUN_RE)) {
        const t = decodeXml(m[1] ?? '').trim();
        if (t)
            out.push(t);
    }
    return out.join(' ').replace(/\s+/g, ' ').trim();
}
function decodeXml(s) {
    return s
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'");
}
//# sourceMappingURL=index.js.map